import { DndContext, DragEndEvent, DragOverlay } from '@dnd-kit/core';
import { arrayMove, horizontalListSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Divider, IconButton, Theme } from '@mui/material';
import { BoxProps } from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import { forwardRef, useState } from 'react';
import { Header } from './Header';

type EditableUserInfoProps = {
  list: string[];
  header: string;
  isEditing: boolean;
  onOrderChange: (newOrder: string[]) => void;
  onSave: () => void;
  onCancel: () => void;
  helperText?: string;
  extraOptions?: React.ReactElement;
  showDivider?: boolean;
};

const UserInfoItem = forwardRef((props: BoxProps & { isEditing?: boolean }, ref) => {
  const forwardedProps = { ...props };
  delete forwardedProps.isEditing;

  return (
    <Box
      {...forwardedProps}
      ref={ref}
      sx={(theme: Theme) => {
        return {
          border: `1px solid ${alpha(theme.palette.border, 0.1)}`,
          borderRadius: theme.shape.borderRadius,
          padding: theme.spacing(1),
          width: 'fit-content',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(0.5),
          cursor: props.isEditing ? 'grab' : 'default',
        };
      }}
    >
      {props.children}
    </Box>
  );
});

type SortableUserInfoItemProps = {
  value: string;
  isEditing: boolean;
};

const SortableUserInfoItem: React.FC<SortableUserInfoItemProps> = ({ value, isEditing }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: value,
    disabled: !isEditing,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <UserInfoItem ref={setNodeRef} style={style} {...attributes} {...(isEditing ? listeners : {})} isEditing={isEditing}>
      <span>{value.toLocaleUpperCase()}</span>
    </UserInfoItem>
  );
};

export const EditableUserInfo: React.FC<EditableUserInfoProps> = ({
  list,
  header,
  isEditing,
  onOrderChange,
  onSave,
  onCancel,
  helperText,
  extraOptions,
  showDivider,
}) => {
  const [draggedItem, setDraggedItem] = useState<string | null>(null);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = list.findIndex(item => item === active.id);
      const newIndex = list.findIndex(item => item === over.id);
      const newOrder = arrayMove(list, oldIndex, newIndex);
      onOrderChange(newOrder);
    }
  };

  return (
    <div>
      <Header header={header} sx={{ marginBottom: '20px' }} helperText={helperText} />

      <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-start' }}>
          {isEditing ? (
            <DndContext
              onDragStart={event => {
                setDraggedItem(event.active.id as string);
              }}
              onDragEnd={event => {
                setDraggedItem(null);
                handleDragEnd(event);
              }}
            >
              <SortableContext items={list} strategy={horizontalListSortingStrategy}>
                {list.map(value => (
                  <SortableUserInfoItem key={value} value={value} isEditing={isEditing} />
                ))}
              </SortableContext>
              <DragOverlay>
                {draggedItem ? (
                  <UserInfoItem isEditing={isEditing}>
                    <span>{draggedItem.toLocaleUpperCase()}</span>
                  </UserInfoItem>
                ) : null}
              </DragOverlay>
            </DndContext>
          ) : (
            list.map(value => (
              <UserInfoItem key={value} isEditing={isEditing}>
                <span>{value.toLocaleUpperCase()}</span>
              </UserInfoItem>
            ))
          )}
        </div>
        <div style={{ display: 'flex', gap: 1 }}>
          {isEditing ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton size="small" onClick={onSave} color="primary">
                <i className="ri-check-line" />
              </IconButton>
              <IconButton size="small" onClick={onCancel} color="error">
                <i className="ri-close-line" />
              </IconButton>
            </Box>
          ) : (
            extraOptions
          )}
        </div>
      </div>
      {showDivider && <Divider sx={{ margin: '20px 0' }} />}
    </div>
  );
};
